import i18n from 'i18next';

export type ProductMoney = Readonly<{
  minorUnits: number;
  currency: string;
}>;

const CURRENCY_SYMBOLS: Readonly<Record<string, string>> = Object.freeze({
  GTQ: 'Q',
});

export function formatProductMoney(
  money: ProductMoney,
  sign: 'plain' | 'income' | 'expense' = 'plain',
): string {
  if (!Number.isSafeInteger(money.minorUnits)) {
    throw new Error('Product money minor units must be a safe integer.');
  }
  const signedMinorUnits =
    sign === 'expense'
      ? -Math.abs(money.minorUnits)
      : sign === 'income'
        ? Math.abs(money.minorUnits)
        : money.minorUnits;
  const prefix = signedMinorUnits < 0 ? '-' : sign === 'income' ? '+' : '';
  const absoluteMinorUnits = Math.abs(signedMinorUnits);
  const whole = Math.floor(absoluteMinorUnits / 100);
  const fraction = String(absoluteMinorUnits % 100).padStart(2, '0');
  const groupedWhole = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const amount =
    fraction === '00' ? groupedWhole : `${groupedWhole}.${fraction}`;
  const currencyLabel = CURRENCY_SYMBOLS[money.currency] ?? money.currency;
  return `${prefix}${currencyLabel} ${amount}`;
}

export function formatMoneyInput(money: ProductMoney): string {
  const absoluteMinorUnits = Math.abs(money.minorUnits);
  const whole = Math.floor(absoluteMinorUnits / 100);
  const fraction = String(absoluteMinorUnits % 100).padStart(2, '0');
  return `${whole}.${fraction}`;
}

export function parseMoneyInput(value: string): number | null {
  const normalized = value.trim().replace(',', '.');
  const match = /^(\d+)(?:\.(\d{0,2}))?$/.exec(normalized);
  if (match === null) {
    return null;
  }
  const whole = Number(match[1]);
  const fraction = Number((match[2] ?? '').padEnd(2, '0'));
  const minorUnits = whole * 100 + fraction;
  return Number.isSafeInteger(minorUnits) && minorUnits > 0 ? minorUnits : null;
}

export function formatProductDate(
  localDate: string,
  locale = i18n.resolvedLanguage || i18n.language || 'en',
): string {
  const [year, month, day] = localDate.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(date);
}

export function getDeviceLocalDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function createFinancialDashboardRange(asOfDate: string): Readonly<{
  asOfDate: string;
  fromDate: string;
  throughDate: string;
  recentFactsLimit: number;
}> {
  const [year, month] = asOfDate.split('-').map(Number);
  return Object.freeze({
    asOfDate,
    fromDate: formatRangeBoundary(new Date(Date.UTC(year - 1, month - 1, 1))),
    throughDate: formatRangeBoundary(
      new Date(Date.UTC(year + 1, month + 6, 0)),
    ),
    recentFactsLimit: 12,
  });
}

function formatRangeBoundary(date: Date): string {
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0'),
  ].join('-');
}
